import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WidgetProps } from '@/lib/widgets/types';
import { DynamicIcon } from '@/components/ui/dynamic-icon';

interface StatsWidgetProps extends WidgetProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: string;
  isLoading?: boolean;
  colorScheme?: 'vehicles' | 'maintenance' | 'alerts' | 'fuel' | 'drivers' | 'trips' | 'default' | 'blue' | 'green' | 'purple' | 'orange' | 'red';
  trend?: {
    value: number;
    label: string;
  };
}

export function StatsWidget({ 
  title, 
  value, 
  description, 
  icon = 'BarChart3',
  colorScheme = 'default',
  trend,
  isLoading = false,
  isEditing = false 
}: StatsWidgetProps) {
  const getColorStyles = (scheme: string) => {
    switch (scheme) {
      case 'vehicles':
        return {
          card: 'border-l-4 border-l-green-500 bg-gradient-to-r from-green-50/50 to-transparent dark:from-green-950/20',
          icon: 'text-green-600 dark:text-green-400',
          accent: 'text-green-700 dark:text-green-300'
        };
      case 'maintenance':
        return {
          card: 'border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-50/50 to-transparent dark:from-orange-950/20',
          icon: 'text-orange-600 dark:text-orange-400',
          accent: 'text-orange-700 dark:text-orange-300'
        };
      case 'alerts':
        return {
          card: 'border-l-4 border-l-red-500 bg-gradient-to-r from-red-50/50 to-transparent dark:from-red-950/20',
          icon: 'text-red-600 dark:text-red-400',
          accent: 'text-red-700 dark:text-red-300'
        };
      case 'fuel':
        return {
          card: 'border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/20',
          icon: 'text-blue-600 dark:text-blue-400',
          accent: 'text-blue-700 dark:text-blue-300'
        };
      case 'drivers':
        return {
          card: 'border-l-4 border-l-cyan-500 bg-gradient-to-r from-cyan-50/50 to-transparent dark:from-cyan-950/20',
          icon: 'text-cyan-600 dark:text-cyan-400',
          accent: 'text-cyan-700 dark:text-cyan-300'
        };
      case 'trips':
        return {
          card: 'border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50/50 to-transparent dark:from-purple-950/20',
          icon: 'text-purple-600 dark:text-purple-400',
          accent: 'text-purple-700 dark:text-purple-300'
        };
      default:
        return {
          card: '',
          icon: 'text-muted-foreground',
          accent: 'text-foreground'
        };
    }
  };

  const colors = getColorStyles(colorScheme);

  return (
    <Card className={`h-full transition-all duration-200 hover:shadow-md ${colors.card}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          {title}
        </CardTitle>
        <DynamicIcon name={icon} className={`h-4 w-4 ${colors.icon}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${colors.accent}`}>{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground">
            {description}
          </p>
        )}
        {trend && (
          <div className="text-xs text-muted-foreground mt-1">
            <span className={trend.value >= 0 ? 'text-green-600' : 'text-red-600'}>
              {trend.value >= 0 ? '+' : ''}{trend.value}%
            </span>
            {' '}{trend.label}
          </div>
        )}
      </CardContent>
    </Card>
  );
}